/**
 * Dialling codes for the signup country picker.
 *
 * This list is not decoration. The country code on a tenant's business mobile
 * is what decides whether they are quoted and charged in INR or USD
 * (serviti-api/src/services/billing_currency.py), so a number typed without one
 * — or with the wrong one — is a billing error, not a formatting one. A select
 * removes the two ways free text got it wrong: omitting the code entirely, and
 * guessing it.
 *
 * CURATED, deliberately — the markets signups realistically come from, not
 * every country. An exhaustive hand-written table was tried and reverted: some
 * two hundred codes typed from memory is a large surface for a mapping that is
 * wrong in a way nothing can detect. A number under the wrong code still
 * validates, still looks right, and reaches a stranger; no test downstream can
 * tell. A short list of codes we can actually vouch for is worth more than a
 * long one we cannot.
 *
 * Anyone from a country that is not here is still served: parseTypedPhone
 * accepts a complete "+" number whatever its dialling code, keeps it whole and
 * leaves the picker unselected. That is what prevents the failure that matters
 * — a foreign number silently becoming +91. What it does not cover is entering
 * a bare local number, which needs the full international form instead.
 *
 * ADDING A COUNTRY: append it, keep `dial` in "+<digits>" form, and add it to
 * KNOWN_DIAL_CODES in tests/test_billing_currency.py so the value is pinned.
 * The same list is copied into the chat widget (PHONE_COUNTRIES in
 * public/embed/serviti-chat-widget.js — ambientWidgetContract.test.js fails
 * until it matches) and into the website (PHONE_COUNTRIES in the website
 * repo's assets/site.js — its lead-funnel test compares the two only when
 * both repositories are checked out side by side, so update it by hand).
 * Add them when customers need them, one verified line at a time.
 *
 * If the whole world ever has to be browsable here, do it with maintained
 * libphonenumber metadata behind a searchable picker — not by hand.
 *
 * United States first and India second for the two primary signup markets; the
 * rest are alphabetical. Ordering also supplies the honest default for a +1
 * number without an explicit country choice: E.164 cannot distinguish the US
 * from Canada, but it must not silently choose Canada over the US.
 */
export const COUNTRY_CODES = [
  { iso: 'US', dial: '+1', name: 'United States' },
  { iso: 'IN', dial: '+91', name: 'India' },

  { iso: 'AR', dial: '+54', name: 'Argentina' },
  { iso: 'AU', dial: '+61', name: 'Australia' },
  { iso: 'AT', dial: '+43', name: 'Austria' },
  { iso: 'BH', dial: '+973', name: 'Bahrain' },
  { iso: 'BD', dial: '+880', name: 'Bangladesh' },
  { iso: 'BE', dial: '+32', name: 'Belgium' },
  { iso: 'BR', dial: '+55', name: 'Brazil' },
  { iso: 'CA', dial: '+1', name: 'Canada' },
  { iso: 'CL', dial: '+56', name: 'Chile' },
  { iso: 'CN', dial: '+86', name: 'China' },
  { iso: 'CO', dial: '+57', name: 'Colombia' },
  { iso: 'CZ', dial: '+420', name: 'Czechia' },
  { iso: 'DK', dial: '+45', name: 'Denmark' },
  { iso: 'EG', dial: '+20', name: 'Egypt' },
  { iso: 'FI', dial: '+358', name: 'Finland' },
  { iso: 'FR', dial: '+33', name: 'France' },
  { iso: 'DE', dial: '+49', name: 'Germany' },
  { iso: 'GH', dial: '+233', name: 'Ghana' },
  { iso: 'GR', dial: '+30', name: 'Greece' },
  { iso: 'HK', dial: '+852', name: 'Hong Kong' },
  { iso: 'ID', dial: '+62', name: 'Indonesia' },
  { iso: 'IE', dial: '+353', name: 'Ireland' },
  { iso: 'IL', dial: '+972', name: 'Israel' },
  { iso: 'IT', dial: '+39', name: 'Italy' },
  { iso: 'JP', dial: '+81', name: 'Japan' },
  { iso: 'KE', dial: '+254', name: 'Kenya' },
  { iso: 'KW', dial: '+965', name: 'Kuwait' },
  { iso: 'MY', dial: '+60', name: 'Malaysia' },
  { iso: 'MU', dial: '+230', name: 'Mauritius' },
  { iso: 'MX', dial: '+52', name: 'Mexico' },
  { iso: 'MA', dial: '+212', name: 'Morocco' },
  { iso: 'NP', dial: '+977', name: 'Nepal' },
  { iso: 'NL', dial: '+31', name: 'Netherlands' },
  { iso: 'NZ', dial: '+64', name: 'New Zealand' },
  { iso: 'NG', dial: '+234', name: 'Nigeria' },
  { iso: 'NO', dial: '+47', name: 'Norway' },
  { iso: 'OM', dial: '+968', name: 'Oman' },
  { iso: 'PK', dial: '+92', name: 'Pakistan' },
  { iso: 'PE', dial: '+51', name: 'Peru' },
  { iso: 'PH', dial: '+63', name: 'Philippines' },
  { iso: 'PL', dial: '+48', name: 'Poland' },
  { iso: 'PT', dial: '+351', name: 'Portugal' },
  { iso: 'QA', dial: '+974', name: 'Qatar' },
  { iso: 'RO', dial: '+40', name: 'Romania' },
  { iso: 'SA', dial: '+966', name: 'Saudi Arabia' },
  { iso: 'SG', dial: '+65', name: 'Singapore' },
  { iso: 'ZA', dial: '+27', name: 'South Africa' },
  { iso: 'KR', dial: '+82', name: 'South Korea' },
  { iso: 'ES', dial: '+34', name: 'Spain' },
  { iso: 'LK', dial: '+94', name: 'Sri Lanka' },
  { iso: 'SE', dial: '+46', name: 'Sweden' },
  { iso: 'CH', dial: '+41', name: 'Switzerland' },
  { iso: 'TW', dial: '+886', name: 'Taiwan' },
  { iso: 'TZ', dial: '+255', name: 'Tanzania' },
  { iso: 'TH', dial: '+66', name: 'Thailand' },
  { iso: 'TR', dial: '+90', name: 'Turkiye' },
  { iso: 'UG', dial: '+256', name: 'Uganda' },
  { iso: 'AE', dial: '+971', name: 'United Arab Emirates' },
  { iso: 'GB', dial: '+44', name: 'United Kingdom' },
  { iso: 'VN', dial: '+84', name: 'Vietnam' },
];

/**
 * Best guess at the visitor's country, from the browser's timezone.
 *
 * Only used to preselect the dropdown — it is a convenience, never a decision.
 * The visible selected country is what gets submitted, so a wrong guess is one
 * click to fix rather than a silent mispricing. Returns null when unsure, which
 * leaves the picker empty and makes the person choose.
 */
export const guessCountryIso = () => {
  let zone = '';
  try {
    zone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (e) {
    return null;
  }

  // Only zones that map to exactly one country are listed. Ambiguous ones are
  // deliberately absent: guessing wrong here decides someone's billing
  // currency, and an empty picker is better than a confident mistake.
  const byZone = {
    'Asia/Kolkata': 'IN', 'Asia/Calcutta': 'IN',
    'Asia/Dubai': 'AE', 'Asia/Riyadh': 'SA', 'Asia/Qatar': 'QA',
    'Asia/Kuwait': 'KW', 'Asia/Muscat': 'OM', 'Asia/Bahrain': 'BH',
    'Asia/Singapore': 'SG', 'Asia/Kuala_Lumpur': 'MY', 'Asia/Jakarta': 'ID',
    'Asia/Manila': 'PH', 'Asia/Bangkok': 'TH', 'Asia/Ho_Chi_Minh': 'VN',
    'Asia/Hong_Kong': 'HK', 'Asia/Tokyo': 'JP', 'Asia/Seoul': 'KR',
    'Asia/Shanghai': 'CN', 'Asia/Taipei': 'TW', 'Asia/Colombo': 'LK',
    'Asia/Dhaka': 'BD', 'Asia/Kathmandu': 'NP', 'Asia/Karachi': 'PK',
    'Asia/Jerusalem': 'IL', 'Asia/Istanbul': 'TR', 'Europe/Istanbul': 'TR',
    'Europe/London': 'GB', 'Europe/Dublin': 'IE', 'Europe/Berlin': 'DE',
    'Europe/Paris': 'FR', 'Europe/Amsterdam': 'NL', 'Europe/Brussels': 'BE',
    'Europe/Madrid': 'ES', 'Europe/Lisbon': 'PT', 'Europe/Rome': 'IT',
    'Europe/Zurich': 'CH', 'Europe/Vienna': 'AT', 'Europe/Stockholm': 'SE',
    'Europe/Oslo': 'NO', 'Europe/Copenhagen': 'DK', 'Europe/Helsinki': 'FI',
    'Europe/Warsaw': 'PL', 'Europe/Prague': 'CZ', 'Europe/Bucharest': 'RO',
    'Europe/Athens': 'GR',
    'Africa/Johannesburg': 'ZA', 'Africa/Lagos': 'NG', 'Africa/Nairobi': 'KE',
    'Africa/Accra': 'GH', 'Africa/Dar_es_Salaam': 'TZ', 'Africa/Kampala': 'UG',
    'Africa/Cairo': 'EG', 'Africa/Casablanca': 'MA', 'Indian/Mauritius': 'MU',
    'Australia/Sydney': 'AU', 'Australia/Melbourne': 'AU',
    'Australia/Brisbane': 'AU', 'Australia/Perth': 'AU',
    'Pacific/Auckland': 'NZ',
    'America/Sao_Paulo': 'BR', 'America/Mexico_City': 'MX',
    'America/Argentina/Buenos_Aires': 'AR', 'America/Santiago': 'CL',
    'America/Bogota': 'CO', 'America/Lima': 'PE',
    'America/New_York': 'US', 'America/Chicago': 'US', 'America/Denver': 'US',
    'America/Los_Angeles': 'US', 'America/Phoenix': 'US',
    'America/Anchorage': 'US', 'Pacific/Honolulu': 'US',
    'America/Toronto': 'CA', 'America/Vancouver': 'CA',
    'America/Edmonton': 'CA', 'America/Winnipeg': 'CA', 'America/Halifax': 'CA',
  };

  return byZone[zone] || null;
};

/**
 * Split a STORED number back into a country and its subscriber digits, so an
 * existing value can be loaded into the picker for editing.
 *
 * Only for values we already persisted. Those are country-coded by definition,
 * which is what licenses the bare-number recovery below. Text a person is
 * typing is NOT that -- see parseTypedPhone, which applies a stricter rule.
 * The two must stay separate: a German national mobile like 15123456789 is
 * eleven digits and would be read here as +1 followed by 5123456789.
 *
 * Longest dial prefix wins, because +1 and +91 would otherwise both match a
 * number starting "+1..." and the shorter one would win by accident. Where two
 * countries genuinely share a code (+1 is US and Canada) the first listed is
 * returned; nothing downstream distinguishes them, since only the dialling code
 * is ever used.
 *
 * Returns { iso, dial, digits } — iso is '' when nothing matches, which puts
 * the picker in its unselected state rather than guessing.
 */
export const splitE164 = (value) => {
  const raw = String(value || '').replace(/[^\d+]/g, '');
  if (!raw) return { iso: '', dial: '', digits: '' };

  // Numbers saved before the "+" was enforced are country-coded but bare, e.g.
  // "919876543210". Reading those as a subscriber number would put the country
  // code into the digits box, and picking a country would then prepend a SECOND
  // one -- "+91" + "919876543210" -- which still looks like a valid E.164
  // number and would silently break a working WhatsApp recipient.
  //
  // So a bare string long enough to carry a country code is matched against the
  // dial codes too. Ten digits or fewer cannot contain one, and is left as a
  // subscriber number with no country selected.
  const hasPlus = raw.startsWith('+');
  const digits = hasPlus ? raw.slice(1) : raw;
  if (!hasPlus && digits.length <= 10) return { iso: '', dial: '', digits };

  const candidate = `+${digits}`;
  let best = null;
  for (const c of COUNTRY_CODES) {
    if (candidate.startsWith(c.dial) && (!best || c.dial.length > best.dial.length)) {
      best = c;
    }
  }
  // A leading "+" with a code the curated list does not carry — a real,
  // complete number from a market we have not added yet. unknownCode says
  // "keep this whole and do not attach a country to it".
  if (!best) return { iso: '', dial: '', digits: candidate.slice(1), unknownCode: hasPlus };
  return { iso: best.iso, dial: best.dial, digits: candidate.slice(best.dial.length) };
};

/**
 * Parse what a person just typed, pasted or let the browser autofill.
 *
 * The rule is deliberately stricter than splitE164's: ONLY a leading "+" means
 * "this carries its own country code". Anything else is subscriber digits for
 * whichever country is selected in the picker.
 *
 * Inferring a country from bare digits is safe for stored values and unsafe
 * here, because national numbering plans overlap dialling codes. Someone in
 * Germany typing their eleven-digit mobile 15123456789 would have it read as
 * +1 5123456789, and the picker would silently jump to the United States and
 * store a number belonging to somebody in North America.
 *
 * Formatting is stripped first, so "+1 (212) 555-0123" parses the same as
 * "+12125550123".
 *
 * Returns { iso, dial, digits }. iso is '' whenever the country must come from
 * the picker rather than from the text.
 */
export const parseTypedPhone = (raw) => {
  let cleaned = String(raw || '').replace(/[^\d+]/g, '');
  // "00" is the international prefix: 0044... is +44..., never the selected
  // country's code plus 44. Read it as "+" before anything else looks at it.
  if (cleaned.startsWith('00')) cleaned = `+${cleaned.slice(2)}`;
  if (!cleaned.startsWith('+')) {
    return { iso: '', dial: '', digits: cleaned.replace(/\D/g, '') };
  }
  const digits = cleaned.slice(1).replace(/\D/g, '');
  let best = null;
  for (const c of COUNTRY_CODES) {
    if (`+${digits}`.startsWith(c.dial) && (!best || c.dial.length > best.dial.length)) {
      best = c;
    }
  }
  // Explicit "+" with a code the curated list does not carry. This is the
  // supported path for an unlisted market, not an edge case: dropping the "+"
  // and letting the caller prefix the selected country turned a pasted "+7..."
  // into "+917..." for anyone with India showing. Keep the number whole and let
  // the validator judge it.
  if (!best) return { iso: '', dial: '', digits, unknownCode: true };
  return { iso: best.iso, dial: best.dial, digits: digits.slice(best.dial.length - 1) };
};

export default COUNTRY_CODES;
